import {
  type LaunchStatusHy,
  type PriceUnitDto,
  PriceUnitType,
  type SimulationData,
} from '@/api';
import type { PriceSettingsDto } from '@/api/models/PriceSettingsDto';
import {
  formatBoolean,
  formatDate,
  formatFactorField,
  formatForecastedDemand,
  formatIndex,
  formatLaunchStatusDisplay,
  formatLpDelta,
  formatPieceCount,
  formatPriceField,
  formatRm,
  formatVat,
  formatYesNo,
  round,
} from '@/components/table/table-format-helpers';
import {
  calculateHasFutureLpChange,
  computeLlLpIndex,
  computeLlRrpIndex,
  computeLp,
  computeLpFmcFactor,
  computeRm,
  computeRrp,
  getDecimals,
  getDisplayDecimals,
  updateLpDerivedFields,
  updateRrpDerivedFields,
} from '@/components/table/table-helpers';
import type {
  EditableField,
  LockableField,
} from '@/components/table/table-types';
import Big from 'big.js';
import type { SimulationIndexRow } from './simulation-index-types';
import type {
  PricelistSettings,
  RowData,
  RowDataForDisplay,
} from './simulation-types';

export enum Corridor {
  Below = 'Below',
  Above = 'Above',
  Within = 'Within',
}

/**
 * Launch statuses that qualify a product for inclusion in portfolio-level key figure
 * aggregations (Sales Revenue, LP/FMC Factor, NIP turnover, etc.). Matches the Excel
 * workbook's `OR(E="C";E="O";E="1ST_HY";E="2ND_HY")` condition.
 */
const KEY_FIGURE_ELIGIBLE_STATUSES: ReadonlySet<LaunchStatusHy> = new Set([
  'CarryOver',
  'Outgoing',
  'FirstHalf',
  'SecondHalf',
] satisfies LaunchStatusHy[]);

/**
 * Whether a row should contribute to portfolio-level key figure aggregations.
 * Mirrors the Excel workbook's per-row eligibility gate:
 *   `AND(FMC > 0, LP > 0, LaunchStatus IN ("C","O","1ST_HY","2ND_HY"))`
 *
 * Products that fail this check are zeroed out in the workbook's SUMPRODUCT / SUMIF
 * formulas, so the frontend must exclude them too for the totals to align.
 */
const isKeyFigureEligible = (row: RowData): boolean =>
  (row.fmcLocal ?? 0) > 0 &&
  (row.lp ?? 0) > 0 &&
  KEY_FIGURE_ELIGIBLE_STATUSES.has(row.launchStatus);

/**
 * Eligibility gate for RRP-based key figures (RRP Index, RRP LL Excl VAT
 * Index).  Matches the Excel workbook's per-row condition on columns
 * ES/ET: AND(RRP > 0, LL RRP > 0, FMC > 0, LaunchStatus eligible).
 *
 * This is intentionally independent of the LP-based `isKeyFigureEligible`
 * gate so that RRP-family indexes use their own "basket" keyed on RRP
 * availability rather than LP availability.
 *
 * Note: RM% uses a different gate — see `computeSimulationIndexes`.
 */
const isRrpKeyFigureEligible = (row: RowData): boolean =>
  (row.rrp ?? 0) > 0 &&
  (row.localLeadRrp ?? 0) > 0 &&
  (row.fmcLocal ?? 0) > 0 &&
  KEY_FIGURE_ELIGIBLE_STATUSES.has(row.launchStatus);

export const getEffectiveFunctionalDiscount = (
  settings: PriceSettingsDto | undefined,
): number =>
  settings?.alternativeFunctionalDiscount ?? settings?.functionalDiscount ?? 0;

export const getEffectiveSalesDiscount = (
  settings: PriceSettingsDto | undefined,
): number => settings?.alternativeSalesDiscount ?? settings?.salesDiscount ?? 0;

//Local RRP Revenue
// RRP * SiPcs
export const calculateTotalRrpRevenue = (rows: RowData[]): number => {
  let total = new Big(0);
  for (const row of rows) {
    const rrp = new Big(row.rrp ?? 0);
    const forecastedDemand = new Big(row.forecastedDemand ?? 0);
    total = total.plus(rrp.times(forecastedDemand));
  }
  return total.toNumber();
};

//LL RRP Revenue
// LL RRP * SiPcs * LL IER
export const calculateTotalLocalLeadRrpRevenue = (
  rows: RowData[],
  pricelistSettings: PricelistSettings,
): number => {
  let total = new Big(0);
  for (const row of rows) {
    const localLeadRrp = new Big(row.localLeadRrp ?? 0);
    const forecastedDemand = new Big(row.forecastedDemand ?? 0);
    const localLeadIer = new Big(pricelistSettings.alternativeFxRate ?? 0);
    total = total.plus(
      localLeadRrp.times(forecastedDemand).times(localLeadIer),
    );
  }
  return total.toNumber();
};

//LL RRP Index
// (total RRP Revenue / total LL RRP Revenue) * 100
export const calculateRrpIndex = (
  rows: RowData[],
  pricelistSettings: PricelistSettings,
): number => {
  const totalRrpRevenue = calculateTotalRrpRevenue(rows);
  const totalLlRrpRevenue = calculateTotalLocalLeadRrpRevenue(
    rows,
    pricelistSettings,
  );
  if (totalLlRrpRevenue === 0) return 0;
  return (totalRrpRevenue / totalLlRrpRevenue) * 100;
};

//Local RRP Excl VAT Revenue
// (RRP / (1 + VAT)) * SiPcs
export const calculateTotalRrpExclVatRevenue = (rows: RowData[]): number => {
  let total = new Big(0);
  for (const row of rows) {
    const rrp = new Big(row.rrp ?? 0);
    const vat = new Big(row.vat ?? 0);
    const forecastedDemand = new Big(row.forecastedDemand ?? 0);
    const divisor = new Big(1).plus(vat);
    total = total.plus(rrp.div(divisor).times(forecastedDemand));
  }
  return total.toNumber();
};

//LL RRP Excl VAT Revenue
//( LL RRP / (1 + LL VAT) * SiPcs * LL IER)
export const calculateTotalLocalLeadRrpExclVatRevenue = (
  rows: RowData[],
  pricelistSettings: PricelistSettings,
): number => {
  let total = new Big(0);
  for (const row of rows) {
    const localLeadRrp = new Big(row.localLeadRrp ?? 0);
    const localLeadVat = new Big(row.localLeadVat ?? 0);
    const divisor = new Big(1).plus(localLeadVat);
    const forecastedDemand = new Big(row.forecastedDemand ?? 0);
    const localLeadIer = new Big(pricelistSettings.alternativeFxRate ?? 0);
    total = total.plus(
      localLeadRrp.div(divisor).times(forecastedDemand).times(localLeadIer),
    );
  }
  return total.toNumber();
};

//LL RRP Excl VAT Index
// (total RRP Excl VAT Revenue / total LL RRP Excl VAT revenue) * 100
export const calculateRrpLocalLeadExclVatIndex = (
  rows: RowData[],
  pricelistSettings: PricelistSettings,
): number => {
  const totalRrpExclVat = calculateTotalRrpExclVatRevenue(rows);
  const totalLocalLeadRrpExclVat = calculateTotalLocalLeadRrpExclVatRevenue(
    rows,
    pricelistSettings,
  );
  if (totalLocalLeadRrpExclVat === 0) return 0;
  return (totalRrpExclVat / totalLocalLeadRrpExclVat) * 100;
};

//LP excl VAT
// (1+vat) * lp * siPcs
export const calculateTotalLpExclVat = (rows: RowData[]): number => {
  let total = new Big(0);
  for (const row of rows) {
    const lp = new Big(row.lp ?? 0);
    const vat = new Big(row.vat ?? 0);
    const forecastedDemand = new Big(row.forecastedDemand ?? 0);
    total = total.plus(new Big(1).plus(vat).times(lp).times(forecastedDemand));
  }
  return total.toNumber();
};

//RM%
//(1 - total LP Excl VAT / total RRP Revenue) * 100
export const calculateRm = (rows: RowData[]): number => {
  const totalLpExclVat = calculateTotalLpExclVat(rows);
  const totalRrpRevenue = calculateTotalRrpRevenue(rows);

  if (totalRrpRevenue === 0) return 0;

  return (1 - totalLpExclVat / totalRrpRevenue) * 100;
};

//LL LP Index
// (total Sales Revenue / total LL Sales Revenue ) * 100
export const calculateTotalLocalLeadLpIndex = (
  rows: RowData[],
  pricelistSettings: PricelistSettings,
): number => {
  const totalSalesRevenue = calculateTotalSalesRevenue(rows, 'lp');
  const totalLocalLeadSalesRevenue = calculateTotalLocalLeadSalesRevenue(
    rows,
    pricelistSettings,
  );

  if (totalLocalLeadSalesRevenue === 0) return 0;
  return (totalSalesRevenue / totalLocalLeadSalesRevenue) * 100;
};

//Local SIM NIP
// LP - (LP * FD) - (SD * (LP - (LP * FD)))
export const calculateNip = (
  lp: number,
  functionalDiscount: number,
  salesDiscount: number,
): number => {
  const bigLp = new Big(lp);
  const fd = new Big(functionalDiscount);
  const sd = new Big(salesDiscount);
  const lpAfterFd = bigLp.minus(bigLp.times(fd));
  return bigLp.minus(bigLp.times(fd)).minus(sd.times(lpAfterFd)).toNumber();
};

//LL NIP
// LL LP - (LL LP * LL FD) - (LL SD * (LL LP - (LL LP * LL FD)))
const calculateLocalLeadNip = (row: RowData): number =>
  calculateNip(
    row.localLeadLp ?? 0,
    row.localLeadFunctionalDiscount ?? 0,
    row.localLeadSalesDiscount ?? 0,
  );

//Local NIP Turnover
// Local SIM NIP * SiPcs
export const calculateTotalNipTurnover = (
  rows: RowData[],
  pricelistSettings: PriceSettingsDto,
): number => {
  let total = new Big(0);
  for (const row of rows) {
    const nip = new Big(
      calculateNip(
        row.lp ?? 0,
        getEffectiveFunctionalDiscount(pricelistSettings),
        getEffectiveSalesDiscount(pricelistSettings),
      ),
    );
    const forecastedDemand = new Big(row.forecastedDemand ?? 0);
    total = total.plus(nip.times(forecastedDemand));
  }
  return total.toNumber();
};

//LL NIP Turnover
// LL NIP * SiPcs * LL IER
export const calculateTotalLocalLeadNipTurnover = (
  rows: RowData[],
  pricelistSettings: PricelistSettings,
): number => {
  let total = new Big(0);
  for (const row of rows) {
    const localLeadNip = new Big(calculateLocalLeadNip(row));
    const forecastedDemand = new Big(row.forecastedDemand ?? 0);
    const localLeadIer = new Big(pricelistSettings.alternativeFxRate ?? 0);
    total = total.plus(
      localLeadNip.times(forecastedDemand).times(localLeadIer),
    );
  }
  return total.toNumber();
};

// (total Local NIP Turnover / total LL NIP Turnover) * 100
export const calculateLocalLeadNipIndex = (
  rows: RowData[],
  pricelistSettings: PricelistSettings,
): number => {
  const totalNipTurnover = calculateTotalNipTurnover(rows, pricelistSettings);
  const totalLocalLeadNipTurnover = calculateTotalLocalLeadNipTurnover(
    rows,
    pricelistSettings,
  );

  if (totalLocalLeadNipTurnover === 0) return 0;

  return (totalNipTurnover / totalLocalLeadNipTurnover) * 100;
};

//LL NIP Index
//(local NIP turnover / LL NIP turnover )* 100
// (local NIP * SiPcs) / (LL NIP * SiPcs * LL IER) * 100
// (local NIP / (LL NIP * LL IER)) * 100
export const calculateLocalLeadNipIndexForRow = (
  nip: number,
  localLeadNip: number,
  localLeadIer: number,
): number => {
  if (localLeadNip === 0 || localLeadIer === 0) return 0;
  return (nip / (localLeadNip * localLeadIer)) * 100;
};

//GL NIP
// GL LP - (GL LP * GL FD) - (GL SD * (GL LP - (GL LP * GL FD)))
export const calculateGlobalLeadNip = (row: RowData): number => {
  return calculateNip(
    row.globalLeadLp ?? 0,
    row.globalLeadFunctionalDiscount ?? 0,
    row.globalLeadSalesDiscount ?? 0,
  );
};

// GL NIP Turnover
// GL NIP * SiPcs * GL IER
export const calculateTotalGlobalLeadNipTurnover = (
  rows: RowData[],
): number => {
  let total = new Big(0);
  for (const row of rows) {
    const globalLeadNip = new Big(calculateGlobalLeadNip(row));
    const forecastedDemand = new Big(row.forecastedDemand ?? 0);
    const globalLeadIer = new Big(row.globalLeadIer ?? 0);
    total = total.plus(
      globalLeadNip.times(forecastedDemand).times(globalLeadIer),
    );
  }
  return total.toNumber();
};

//GL NIP Index
// (total Local NIP Turnover / total GL NIP Turnover) * 100
export const calculateGlobalLeadNipIndex = (
  rows: RowData[],
  pricelistSettings: PriceSettingsDto,
): number => {
  const totalNipTurnover = calculateTotalNipTurnover(rows, pricelistSettings);
  const totalGlobalLeadNipTurnover = calculateTotalGlobalLeadNipTurnover(rows);

  if (totalGlobalLeadNipTurnover === 0) return 0;

  return (totalNipTurnover / totalGlobalLeadNipTurnover) * 100;
};

// Total Sales Revenue
export const calculateTotalSalesRevenue = (
  rows: RowData[],
  lpField: 'lp' | 'asIsLp',
): number => {
  let total = new Big(0);
  for (const row of rows) {
    total = total.plus(
      new Big(row[lpField] ?? 0).times(new Big(row.forecastedDemand ?? 0)),
    );
  }
  return total.toNumber();
};

//LL Sales Revenue
// LL LP * SiPcs * LL IER
export const calculateTotalLocalLeadSalesRevenue = (
  rows: RowData[],
  pricelistSettings: PricelistSettings,
): number => {
  let total = new Big(0);
  for (const row of rows) {
    const localLeadLp = new Big(row.localLeadLp ?? 0);
    const forecastedDemand = new Big(row.forecastedDemand ?? 0);
    const localLeadIer = new Big(pricelistSettings.alternativeFxRate ?? 0);
    total = total.plus(localLeadLp.times(forecastedDemand).times(localLeadIer));
  }
  return total.toNumber();
};

//Total FMC
// Sales Revenue - (FMC Local * SiPcs)
export const calculateTotalTotalFmc = (
  rows: RowData[],
  lpField: 'lp' | 'asIsLp' = 'lp',
): number => {
  let total = new Big(0);
  for (const row of rows) {
    const lp = new Big(row[lpField] ?? 0);
    const fmcLocal = new Big(row.fmcLocal ?? 0);
    const forecastedDemand = new Big(row.forecastedDemand ?? 0);
    const salesRevenue = lp.times(forecastedDemand);
    total = total.plus(salesRevenue.minus(fmcLocal.times(forecastedDemand)));
  }
  return total.toNumber();
};

//LP/FMC Factor
// salesRevenue / (salesRevenue - fmc)
const computeTotalLpFmcFactor = (
  totalSalesRevenue: number,
  totalFmc: number,
): number => {
  if (totalFmc === 0) return 0;
  return totalSalesRevenue / (totalSalesRevenue - totalFmc);
};

export const calculateLpFmcFactor = (rows: RowData[]): number => {
  return computeTotalLpFmcFactor(
    calculateTotalSalesRevenue(rows, 'lp'),
    calculateTotalTotalFmc(rows, 'lp'),
  );
};

//LP Delta
// (LP - Previous LP) / Previous LP
export const calculateLpDelta = (lp: number, lpLocal: number): number => {
  if (lpLocal === 0) return 0;
  return new Big(lp).minus(lpLocal).div(lpLocal).toNumber();
};

//Previous (As-Is) LP/FMC Factor
// Own eligibility basket: excludes products unpriced at simulation creation
// (asIsLp = 0) from both numerator and denominator.
export const calculatePreviousLpFmcFactor = (rows: RowData[]): number => {
  const asIsEligible = rows.filter(
    (row) =>
      (row.asIsLp ?? 0) > 0 &&
      (row.fmcLocal ?? 0) > 0 &&
      KEY_FIGURE_ELIGIBLE_STATUSES.has(row.launchStatus),
  );
  return computeTotalLpFmcFactor(
    calculateTotalSalesRevenue(asIsEligible, 'asIsLp'),
    calculateTotalTotalFmc(asIsEligible, 'asIsLp'),
  );
};

//LP/FMC Factor Difference
// ((new - old) / new) * 100
export const calculateLpFmcFactorDifference = (rows: RowData[]): number => {
  const lpFmcFactor = calculateLpFmcFactor(rows);
  const previousLpFmcFactor = calculatePreviousLpFmcFactor(rows);
  if (lpFmcFactor === 0) return 0;
  return ((lpFmcFactor - previousLpFmcFactor) / lpFmcFactor) * 100;
};

// Extends updateLpDerivedFields with simulation-specific derived fields:
// lpDelta, nip, llNip
export const updateSimulationLpDerivedFields = (
  updated: RowData,
  row: RowData,
  localLeadIer: number,
  pricelistSettings?: PriceSettingsDto,
): void => {
  updateLpDerivedFields(updated, row, localLeadIer);
  updated.lpDelta = calculateLpDelta(updated.lp, row.asIsLp);
  if (pricelistSettings) {
    updated.nip = calculateNip(
      updated.lp,
      getEffectiveFunctionalDiscount(pricelistSettings),
      getEffectiveSalesDiscount(pricelistSettings),
    );
    updated.llNip = calculateNip(
      updated.localLeadLp,
      updated.localLeadFunctionalDiscount,
      updated.localLeadSalesDiscount,
    );
    updated.llNipIndex = calculateLocalLeadNipIndexForRow(
      updated.nip,
      updated.llNip,
      localLeadIer,
    );
  }
};

//FMC Local
// FMCCostPerPiece * globalExchangeRate
export const calculateFmcLocal = (
  fmcCostPerPiece: number,
  exchangeRate: number,
): number => {
  return fmcCostPerPiece * exchangeRate;
};

//average VAT
export const calculateAverageVat = (rows: RowData[]): number => {
  const vats = rows.map((r) => r.vat);
  return vats.length === 0
    ? 0
    : round(vats.reduce((a, b) => a + b, 0) / vats.length, 2);
};

export const groupDataByPriceList = (
  data: RowData[],
): Record<string, RowData[]> => {
  const grouped: Record<string, RowData[]> = {};
  for (const value of data) {
    const priceList = value.priceUnitId || '';
    if (!grouped[priceList]) {
      grouped[priceList] = [];
    }
    grouped[priceList].push(value);
  }
  return grouped;
};

export const formatCorridorMark = (corridor: Corridor | null): string => {
  if (corridor === Corridor.Above) return 'ABOVE';
  if (corridor === Corridor.Below) return 'BELOW';
  return '';
};

export const formatDataForDisplay = (
  dataToFormat: RowData[],
  pricelistSettings: Map<string, PricelistSettings>,
  totalLlRrpIndex: Map<string, number>,
): RowDataForDisplay[] => {
  return Array.from(dataToFormat.values()).map((row) => {
    const corridor = getPpcCorridor(row, pricelistSettings, totalLlRrpIndex);
    return {
      ...row,
      isRareItem: formatYesNo(row.isRareItem),
      businessToBusinessLaunchDate: formatDate(
        row.businessToBusinessLaunchDate,
      ),
      localLaunchDate: formatDate(row.localLaunchDate),
      localExitDate: formatDate(row.localExitDate),
      directToConsumerLifelong: formatYesNo(row.directToConsumerLifelong),
      pieceCount: formatPieceCount(row.pieceCount),
      launchStatus: formatLaunchStatusDisplay(row.launchStatus),
      localLeadRrp: formatPriceField(row.localLeadRrp),
      localLeadRm: formatRm(row.localLeadRm),
      rrpIndexLocalLead: formatIndex(row.rrpIndexLocalLead),
      asIsRm: formatRm(row.asIsRm),
      asIsRrp: formatPriceField(row.asIsRrp, false, getDisplayDecimals(row)),
      asIsLp: formatPriceField(row.asIsLp, false, getDisplayDecimals(row)),
      rm: formatRm(row.rm),
      localLeadLp: formatPriceField(row.localLeadLp),
      lp: formatPriceField(row.lp, true, getDisplayDecimals(row)),
      rrp: formatPriceField(row.rrp, false, getDisplayDecimals(row)),
      lpIndexLocalLead: formatIndex(row.lpIndexLocalLead),
      lpDelta: formatLpDelta(row.lpDelta),
      nip: formatPriceField(row.nip),
      llNip: formatPriceField(row.llNip),
      llNipIndex: formatIndex(row.llNipIndex),
      hasFutureLpChange: formatYesNo(row.hasFutureLpChange),
      listPriceFmcFactor: formatFactorField(row.listPriceFmcFactor),
      fmcLocal: formatPriceField(row.fmcLocal),
      forecastedDemand: formatForecastedDemand(row.forecastedDemand),
      vat: formatVat(row.vat),
      hasPriceChange: formatBoolean(row.hasPriceChange),
      ppcMark: formatCorridorMark(corridor),
    };
  });
};

export const recalculateFields = (
  simulationData: Map<string, RowData>,
  pricelistSettings: Map<string, PricelistSettings>,
): Map<string, RowData> => {
  const newData = new Map<string, RowData>();
  let hasChanges = false;

  for (const [key, row] of simulationData.entries()) {
    const settings = pricelistSettings.get(row.priceUnitId.toString());
    const localLeadIer = settings?.alternativeFxRate ?? 0;

    const updatedRow = { ...row };

    //on IER update, recalculate indexes
    const newRrpIndexLocalLead = computeLlRrpIndex(
      updatedRow.rrp,
      updatedRow.localLeadRrp,
      localLeadIer,
    );
    const newLpIndexLocalLead = computeLlLpIndex(
      updatedRow.lp,
      updatedRow.localLeadLp,
      localLeadIer,
    );
    const newLpDelta = calculateLpDelta(updatedRow.lp, row.asIsLp);
    const newNip = settings
      ? calculateNip(
          updatedRow.lp,
          getEffectiveFunctionalDiscount(settings),
          getEffectiveSalesDiscount(settings),
        )
      : 0;
    const newLlNip = settings
      ? calculateNip(
          updatedRow.localLeadLp,
          updatedRow.localLeadFunctionalDiscount,
          updatedRow.localLeadSalesDiscount,
        )
      : 0;
    const newLlNipIndex = calculateLocalLeadNipIndexForRow(
      newNip,
      newLlNip,
      localLeadIer,
    );

    const newListPriceFmcFactor = computeLpFmcFactor(
      updatedRow.lp,
      updatedRow.fmcLocal,
    );

    if (
      newRrpIndexLocalLead !== row.rrpIndexLocalLead ||
      newLpIndexLocalLead !== row.lpIndexLocalLead ||
      newLpDelta !== row.lpDelta ||
      newNip !== row.nip ||
      newLlNip !== row.llNip ||
      newLlNipIndex !== row.llNipIndex ||
      newListPriceFmcFactor !== row.listPriceFmcFactor
    ) {
      hasChanges = true;
    }

    newData.set(key, {
      ...updatedRow,
      rrpIndexLocalLead: newRrpIndexLocalLead,
      lpIndexLocalLead: newLpIndexLocalLead,
      lpDelta: newLpDelta,
      nip: newNip,
      llNip: newLlNip,
      llNipIndex: newLlNipIndex,
      listPriceFmcFactor: newListPriceFmcFactor,
    });
  }

  return hasChanges ? newData : simulationData;
};

export const calculatePricingField = (
  field: EditableField,
  row: RowData,
  value: number,
  lockedColumn: LockableField | undefined,
  pricelistSettings?: Map<string, PricelistSettings>,
): RowData => {
  const fieldMap = {
    rrp: 'rrp',
    rm: 'rm',
    lp: 'lp',
    vat: 'vat',
  } as const;
  const actualField = fieldMap[field];

  // Round an entered LP/RRP first; every dependent value (LP, RRP, RM) is calculated from
  // the rounded value. RM/VAT entries are used as typed.
  const decimals = getDecimals(row);
  const committed =
    field === 'lp' || field === 'rrp' ? round(value, decimals) : value;

  // avoid unnecessary recalculation if the value is the same
  if (row[actualField] === committed) {
    return row;
  }

  const settings = pricelistSettings?.get(row.priceUnitId.toString());
  const localLeadIer = settings?.alternativeFxRate ?? 0;
  const updated = { ...row, [actualField]: committed };
  const isDistributionPriceList = !row.useRrp;

  switch (lockedColumn) {
    case 'rrp':
      if (field === 'lp' && !isDistributionPriceList) {
        updated.rm = computeRm(row.rrp, committed, row.vat);
      } else if (field === 'rm') {
        updated.lp = computeLp(row.rrp, committed, row.vat);
      } else if (field === 'vat') {
        updated.rm = computeRm(row.rrp, row.lp, committed);
      }
      break;

    case 'rm':
      if (field === 'rrp') {
        updated.lp = computeLp(committed, row.rm, row.vat);
      } else if (field === 'lp' && !isDistributionPriceList) {
        updated.rrp = computeRrp(committed, row.rm, row.vat);
      } else if (field === 'vat') {
        updated.lp = computeLp(row.rrp, row.rm, committed);
      }
      break;

    case 'lp':
      if (field === 'rrp') {
        updated.rm =
          committed === 0 ? 0 : computeRm(committed, row.lp, row.vat);
      } else if (field === 'rm') {
        updated.rrp = computeRrp(row.lp, committed, row.vat);
      } else if (field === 'vat') {
        updated.rm = computeRm(row.rrp, row.lp, committed);
      }
      break;

    default: //no locked field
      if (field === 'rrp') {
        if (committed === 0) {
          updated.rm = 0;
        } else {
          updated.lp = computeLp(committed, row.rm, row.vat);
        }
      } else if (field === 'rm') {
        updated.lp = computeLp(row.rrp, committed, row.vat);
      } else if (field === 'lp' && !isDistributionPriceList) {
        updated.rm = computeRm(row.rrp, committed, row.vat);
      } else if (field === 'vat') {
        updated.rm = computeRm(row.rrp, row.lp, committed);
      }
      break;
  }

  // Round the calculated price; RM is kept as calculated
  if (updated.lp !== row.lp) updated.lp = round(updated.lp, decimals);
  if (updated.rrp !== row.rrp) updated.rrp = round(updated.rrp, decimals);

  // update derived fields if lp or rrp has changed
  updateSimulationLpDerivedFields(updated, row, localLeadIer, settings);

  if (updated.rrp !== row.rrp) {
    updateRrpDerivedFields(updated, row, localLeadIer);
  }

  updated.hasFutureLpChange = calculateHasFutureLpChange(
    updated.lp,
    updated.nearestFutureModifiedLp,
  );

  return updated;
};

export const getPpcCorridor = (
  row: RowData,
  pricelistSettings: Map<string, PricelistSettings>,
  totalLlRrpIndex: Map<string, number>,
): Corridor | null => {
  const settings = pricelistSettings.get(row.priceUnitId.toString());
  if (!settings) return null;

  const totalIndex = totalLlRrpIndex.get(row.priceUnitId.toString()) ?? 0;
  if (row.rrpIndexLocalLead === 0 || totalIndex === 0) return null;

  let lowParameter = 0;
  let highParameter = 0;

  if (row.localLeadRrp > (settings?.thresholdPrice ?? 0)) {
    lowParameter = settings.thresholdBelowMin ?? 0;
    highParameter = settings.thresholdBelowMax ?? 0;
  } else {
    lowParameter = settings.thresholdAboveMin ?? 0;
    highParameter = settings.thresholdAboveMax ?? 0;
  }
  const lowerBoundIndex = totalIndex - lowParameter;
  const upperBoundIndex = totalIndex + highParameter;

  if (row.rrpIndexLocalLead < lowerBoundIndex) {
    return Corridor.Below;
  }
  if (row.rrpIndexLocalLead > upperBoundIndex) {
    return Corridor.Above;
  }
  return Corridor.Within;
};

/**
 * Frozen numeric fields are set at simulation creation and cannot be changed
 * afterwards. The backend returns them as 0 in preview (version) data as a
 * sentinel value, so we fall back to the live simulation row value when that
 * happens.
 */
const FROZEN_NUMERIC_FIELDS: ReadonlyArray<keyof SimulationData> = [
  'fmcCostPerPiece',
  'globalLeadCurrencyRate',
  'localLeadCurrencyRate',
  'localLeadRrp',
  'localLeadLp',
  'localLeadRm',
  'localLeadVat',
  'localLeadFunctionalDiscount',
  'localLeadSalesDiscount',
  'localLeadTradeContributionDiscount',
  'globalLeadRrp',
  'globalLeadLp',
  'globalLeadRm',
  'globalLeadVat',
  'globalLeadFunctionalDiscount',
  'globalLeadSalesDiscount',
  'globalLeadTradeContributionDiscount',
  'globalLeadIer',
  'forecastedDemand',
  'vat',
  'functionalDiscount',
  'salesDiscount',
  'tradeContributionDiscount',
  'previousRrp',
  'previousLp',
  'previousRm',
  'previousVat',
  'previousLocalLeadRrp',
  'previousLocalLeadLp',
  'previousLocalLeadRm',
  'previousLocalLeadVat',
  'previousGlobalLeadRrp',
  'previousGlobalLeadLp',
  'previousGlobalLeadRm',
  'previousGlobalLeadVat',
  'previousLocalLeadCurrencyRate',
  'previousGlobalLeadCurrencyRate',
  'previousForecastedDemand',
  'previousFmcCostPerPiece',
  'listPriceFmcTargetFactor',
  'aboveThresholdMin',
  'aboveThresholdMax',
  'belowThresholdMin',
  'belowThresholdMax',
  'thresholdPrice',
  'futureRrp',
  'futureLp',
  'futureRm',
  'futureVat',
] as const;

/**
 * Merges a preview version row with the corresponding live simulation row.
 * For each frozen numeric field, if the preview value is 0 (the backend
 * sentinel for immutable/frozen fields), the live row value is substituted
 * so that downstream display and calculations remain correct.
 *
 * User-editable price fields (rrp, lp, rm) are intentionally excluded and
 * always taken from the preview row, even when they are 0.
 */
export const mergePreviewRowWithCurrentRow = (
  previewRow: SimulationData,
  currentRow: SimulationData | undefined,
): SimulationData => {
  if (!currentRow) return previewRow;

  const overrides = Object.fromEntries(
    FROZEN_NUMERIC_FIELDS.filter(
      (field) =>
        (previewRow[field] as number) === 0 && currentRow[field] !== undefined,
    ).map((field) => [field, currentRow[field]]),
  );
  return { ...previewRow, ...overrides };
};

/**
 * Builds a priceUnitId → PricelistSettings map from a version's saved price
 * settings, computing averageVat from that version's simulation data rows.
 * Returns null when either input is missing (query not yet resolved).
 */
export const buildPricelistSettingsMap = (
  priceSettings: PriceSettingsDto[] | null | undefined,
  simulationRows: SimulationData[] | null | undefined,
): Map<string, PricelistSettings> | null => {
  if (!priceSettings || !simulationRows) return null;
  const groupedData = groupDataByPriceList(
    simulationRows as unknown as RowData[],
  );
  return new Map(
    priceSettings.map((setting) => {
      const priceUnitId = (setting.priceUnitId ?? 0).toString();
      return [
        priceUnitId,
        {
          ...setting,
          averageVat: calculateAverageVat(groupedData[priceUnitId] || []),
        } as PricelistSettings,
      ];
    }),
  );
};

const getAlternativeFxRate = (
  pricelistSettings: Map<string, PricelistSettings>,
  priceUnitId: number | string | null | undefined,
): number => {
  const s = pricelistSettings.get((priceUnitId ?? '0').toString());
  return s?.alternativeFxRate ?? 1;
};

const computeNipFields = (
  pricelistSettings: Map<string, PricelistSettings>,
  row: SimulationData,
): { nip: number; llNip: number; llNipIndex: number } => {
  const settings = pricelistSettings.get((row.priceUnitId ?? '0').toString());
  const nip = settings
    ? calculateNip(
        row.lp ?? 0,
        getEffectiveFunctionalDiscount(settings),
        getEffectiveSalesDiscount(settings),
      )
    : 0;
  const llNip = calculateNip(
    row.localLeadLp ?? 0,
    row.localLeadFunctionalDiscount ?? 0,
    row.localLeadSalesDiscount ?? 0,
  );
  return {
    nip,
    llNip,
    llNipIndex: calculateLocalLeadNipIndexForRow(
      nip,
      llNip,
      settings?.alternativeFxRate ?? 1,
    ),
  };
};

/**
 * Maps raw SimulationData rows to RowData by computing all derived display
 * fields using the given pricelist settings map.
 */
export const mapSimulationDataToRowData = (
  data: SimulationData[],
  pricelistSettings: Map<string, PricelistSettings>,
): RowData[] => {
  return (
    data?.map(
      (item) =>
        ({
          ...item,
          productCount: item.productId,
          useRrp: item.useRrp ?? true,
          asIsRrp: item.initialRrp ?? 0,
          asIsRm: item.initialRm ?? 0,
          asIsLp: item.initialLp ?? 0,
          hasPriceChange: false,
          hasFutureLpChange: calculateHasFutureLpChange(
            item.lp ?? 0,
            item.nearestFutureModifiedLp,
          ),
          isExemptFromFiltering: false,
          rrpIndexLocalLead: computeLlRrpIndex(
            item.rrp || 0,
            item.localLeadRrp || 0,
            getAlternativeFxRate(pricelistSettings, item.priceUnitId),
          ),
          listPriceFmcFactor: computeLpFmcFactor(
            item.lp || 0,
            calculateFmcLocal(
              item.fmcCostPerPiece || 0,
              item.globalLeadCurrencyRate || 0,
            ),
          ),
          lpIndexLocalLead: computeLlLpIndex(
            item.lp || 0,
            item.localLeadLp || 0,
            getAlternativeFxRate(pricelistSettings, item.priceUnitId),
          ),
          lpDelta: calculateLpDelta(item.lp || 0, item.previousLp || 0),
          ...computeNipFields(pricelistSettings, item),
          license: item.license ? 'Yes' : 'No',
          fmcLocal: calculateFmcLocal(
            item.fmcCostPerPiece || 0,
            item.globalLeadCurrencyRate || 0,
          ),
          ppcMark: '',
        }) as RowData,
    ) || []
  );
};

/**
 * Builds a baseline map where editable price fields (lp, rrp, rm) are replaced
 * with their immutable creation-time values (asIsLp, asIsRrp, asIsRm).
 * Used for session-proof revert and change detection — survives browser refresh
 * because asIs* values come from backend's immutable initialLp/initialRrp/initialRm.
 */
export const buildOriginalBaselineMap = (
  initialData: Map<string, RowData>,
): Map<string, RowData> => {
  const baseline = new Map<string, RowData>();
  for (const [key, row] of initialData) {
    baseline.set(key, {
      ...row,
      lp: row.asIsLp,
      rrp: row.asIsRrp,
      rm: row.asIsRm,
    });
  }
  return baseline;
};

/** Fields tracked for row-level pink highlighting in version preview mode. */
export const PREVIEW_TRACKED_FIELDS: ReadonlyArray<keyof RowData> = [
  'rrp',
  'rm',
  'lp',
  'vat',
  'comment',
];

/**
 * Compare raw RowData values by Map lookup — avoids false positives from
 * formatted-string comparisons or derived field differences.
 * Only the specific field for the given column is compared.
 */
export const isCellChangedInPreview = (
  row: RowDataForDisplay,
  columnKey: keyof RowData,
  previewBaseData: Map<string, RowData> | undefined,
  dataWithRecalculatedFields: Map<string, RowData>,
): boolean => {
  if (!previewBaseData) return false;
  const baseRow = previewBaseData.get(row.primaryKey as string);
  if (!baseRow) return false;
  const displayRow = dataWithRecalculatedFields.get(row.primaryKey as string);
  if (!displayRow) return false;
  // Only highlight if the key exists on both
  if (!(columnKey in baseRow) || !(columnKey in displayRow)) return false;
  return (
    (displayRow as Record<string, unknown>)[columnKey as string] !==
    (baseRow as Record<string, unknown>)[columnKey as string]
  );
};

/**
 * Returns true if any tracked field on the row differs between the
 * preview version and the live base data, used to highlight the entire row.
 */
export const isRowChangedInPreview = (
  row: RowDataForDisplay,
  previewBaseData: Map<string, RowData> | undefined,
  dataWithRecalculatedFields: Map<string, RowData>,
): boolean => {
  if (!previewBaseData) return false;
  const baseRow = previewBaseData.get(row.primaryKey as string);
  if (!baseRow) return false;
  const displayRow = dataWithRecalculatedFields.get(row.primaryKey as string);
  if (!displayRow) return false;
  return PREVIEW_TRACKED_FIELDS.some(
    (key) =>
      key in baseRow &&
      key in displayRow &&
      (displayRow as Record<string, unknown>)[key as string] !==
        (baseRow as Record<string, unknown>)[key as string],
  );
};

export const computeSimulationIndexes = (
  data: RowData[],
  pricelistSettingsData: Map<string, PricelistSettings>,
  priceUnits: PriceUnitDto[],
): SimulationIndexRow[] => {
  const groupedData = groupDataByPriceList(data);
  return Object.entries(groupedData).map(([priceList, items]) => {
    const settings = pricelistSettingsData.get(priceList);
    // Filter to products eligible for key figure aggregation, matching the Excel
    // workbook's per-row gate: FMC > 0, LP > 0, and an active launch status.
    const eligible = items.filter(isKeyFigureEligible);
    // RRP-family indexes use their own basket keyed on RRP/LL RRP/FMC
    // availability (Excel columns ES/ET gate), independent of the LP gate.
    const rrpEligible = items.filter(isRrpKeyFigureEligible);
    // RM% uses the LP-eligible basket plus an RRP > 0 guard, matching
    // the Excel EC column gate: AND(LP > 0, RRP > 0, FMC > 0, status).
    const rmEligible = eligible.filter((row) => (row.rrp ?? 0) > 0);
    return {
      priceList: priceUnits.find((pu) => pu.id.toString() === priceList) ?? {
        id: Number(priceList),
        name: null,
        type: PriceUnitType.Country,
      },
      currency: items[0]?.currency || '',
      rrpIndex: settings ? calculateRrpIndex(rrpEligible, settings) : 0,
      rrpLocalLeadExclVatIndex: settings
        ? calculateRrpLocalLeadExclVatIndex(rrpEligible, settings)
        : 0,
      rm: calculateRm(rmEligible),
      lpLocalLeadIndex: settings
        ? calculateTotalLocalLeadLpIndex(eligible, settings)
        : 0,
      nipLocalLeadIndex: settings
        ? calculateLocalLeadNipIndex(eligible, settings)
        : 0,
      nipGlobalLeadIndex: settings
        ? calculateGlobalLeadNipIndex(eligible, settings)
        : 0,
      listPriceFmcFactorTarget: settings?.listPriceFmcFactorTarget ?? 0,
      listPriceFmcFactor: calculateLpFmcFactor(eligible),
      listPriceFmcFactorDifference: calculateLpFmcFactorDifference(eligible),
      salesRevenue: calculateTotalSalesRevenue(eligible, 'lp'),
      // Total product count includes all products, not just key-figure-eligible ones.
      productCount: items.length,
    };
  });
};

//LP C&O Turnover
// LP * SiPcs
export const calculateTotalLpCoTurnover = (rows: RowData[]): number => {
  let totalLpCoTurnover = 0;
  for (const row of rows) {
    const lp = row.lp ?? 0;
    const forecastedDemand = row.forecastedDemand ?? 0;
    totalLpCoTurnover +=
      row.launchStatus === 'CarryOver' || row.launchStatus === 'Outgoing'
        ? lp * forecastedDemand
        : 0;
  }
  return totalLpCoTurnover;
};

//Previous LP C&O Turnover
// Previous LP * SiPcs
export const calculateTotalPreviousLpCoTurnover = (rows: RowData[]): number => {
  let totalLpCoTurnover = 0;
  for (const row of rows) {
    const lpPrevious = row.asIsLp ?? 0; //todo: needs a double check once we have the approval flow in place
    const forecastedDemand = row.forecastedDemand ?? 0;
    totalLpCoTurnover +=
      row.launchStatus === 'CarryOver' || row.launchStatus === 'Outgoing'
        ? lpPrevious * forecastedDemand
        : 0;
  }
  return totalLpCoTurnover;
};

//LP C&O Δ
//(LP C&O Turnover - Previous LP C&O Turnover) / Previous LP C&O Turnover
export const calculateLpCoIncrease = (rows: RowData[]): number => {
  const totalLpCoTurnover = calculateTotalLpCoTurnover(rows);
  const totalPreviousLpCoTurnover = calculateTotalPreviousLpCoTurnover(rows);

  if (totalPreviousLpCoTurnover === 0) return 0;
  return round(
    (totalLpCoTurnover - totalPreviousLpCoTurnover) / totalPreviousLpCoTurnover,
    2,
  );
};

//Local SIM NRU
//NIP * (1 - TCD)
const calculateNru = (nip: number, tcd: number): number => {
  return nip * (1 - tcd);
};

//Local NRU Turnover
//Local NRU *SiPcs
const calculateTotalLocalNruTurnover = (
  rows: RowData[],
  pricelistSettings: PricelistSettings,
): number => {
  let totalLocalNruTurnover = 0;
  for (const row of rows) {
    const nip = calculateNip(
      row.lp ?? 0,
      getEffectiveFunctionalDiscount(pricelistSettings),
      getEffectiveSalesDiscount(pricelistSettings),
    );
    const localNru = calculateNru(nip, row.tradeContributionDiscount ?? 0);
    const forecastedDemand = row.forecastedDemand ?? 0;
    totalLocalNruTurnover += localNru * forecastedDemand;
  }
  return totalLocalNruTurnover;
};

//LL NRU Turnover
//LL NRU * SiPcs * LL IER
const calculateTotalLocalLeadNruTurnover = (
  rows: RowData[],
  pricelistSettings: PricelistSettings,
): number => {
  let totalLocalLeadNruTurnover = 0;
  for (const row of rows) {
    const localLeadNip = calculateNip(
      row.localLeadLp ?? 0,
      row.localLeadFunctionalDiscount ?? 0,
      row.localLeadSalesDiscount ?? 0,
    );
    const localLeadNru = calculateNru(
      localLeadNip,
      row.localLeadTradeContributionDiscount ?? 0,
    );
    const forecastedDemand = row.forecastedDemand ?? 0;
    const localLeadIer = new Big(pricelistSettings.alternativeFxRate ?? 0);
    totalLocalLeadNruTurnover +=
      localLeadNru * forecastedDemand * localLeadIer.toNumber();
  }
  return totalLocalLeadNruTurnover;
};

//LL NRU Index
// (totalLocalLeadNruTurnover / totalLocalNruTurnover) * 100
export const calculateLocalLeadNruIndex = (
  rows: RowData[],
  pricelistSettings: PricelistSettings,
): number => {
  const totalLocalLeadNruTurnover = calculateTotalLocalLeadNruTurnover(
    rows,
    pricelistSettings,
  );
  const totalLocalNruTurnover = calculateTotalLocalNruTurnover(
    rows,
    pricelistSettings,
  );

  if (totalLocalNruTurnover === 0) return 0;
  return round((totalLocalLeadNruTurnover / totalLocalNruTurnover) * 100, 2);
};
